/**
 * Centralized stream options factory for all chat pages.
 *
 * The new equivalent of the old useChatOptions/useProjectChatOptions.
 * Handles JWT, apiUrl, headers, threadId safety, reconnect defaults,
 * onUpdateEvent for subgraph intermediate state, and named store registration.
 *
 * Pages only specify what differs: assistantId, onThreadId, and
 * optionally a custom threadId resolver.
 *
 * @example
 * ```ts
 * const streamOptions = useProjectStreamOptions<WebsiteGraphState>({
 *   assistantId: "website",
 *   onThreadId: (id) => router.push({ ... }),
 * });
 *
 * <Chat.Root streamOptions={streamOptions}>...</Chat.Root>
 * ```
 */
import type { useLanggraphStreamOptions } from "@launch10/use-langgraph-stream";
import { usePage } from "@inertiajs/react";
import { useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { artifactsMerge, type Artifact } from "@shared";
import { useJwt, useLanggraphPath } from "~/stores/sessionStore";

// --- Reducers for onUpdateEvent ---
// Replace: new value wins (default for scalars and fully-recomputed arrays)
const replace = (_prev: unknown, next: unknown) => next;
// Merge by key: upsert into existing array by a unique field.
// Mirrors server-side Deploy::STEPS task ordering.
// Terminal-state guard: never downgrade completed/failed/skipped → running/pending.
const TERMINAL_STATUSES = new Set(["completed", "failed", "skipped"]);

export const mergeByKey =
  (key: string) => (prev: unknown, next: unknown) => {
    if (!Array.isArray(next) || !Array.isArray(prev)) return next;
    const map = new Map(prev.map((item: any) => [item[key], item]));
    for (const item of next as any[]) {
      const existing = map.get(item[key]);
      if (existing?.status && TERMINAL_STATUSES.has(existing.status) &&
          item.status && !TERMINAL_STATUSES.has(item.status)) {
        continue; // Don't downgrade terminal → non-terminal
      }
      map.set(item[key], { ...existing, ...item });
    }
    return Array.from(map.values());
  };

/**
 * Declarative reducer map for streaming state keys.
 *
 * The client has no channel reducers of its own. With `streamSubgraphs: true`
 * the SDK ships each scope's raw, partial state mid-run and folds it by
 * wholesale replace; the graph's server-side reducers only settle the value at
 * the parent merge. This map mirrors those reducers for the streaming window so
 * channels written by multiple scopes (parallel subagent todos, file maps)
 * accumulate instead of clobbering each other. Keys absent here fall back to
 * `replace` — the right default for scalars and fully-recomputed values.
 *
 * To stream a new property, register its reducer here (or per-chat via
 * `streamingReducers`). Never hand-roll a bespoke onUpdateEvent for folding.
 */
export type StreamReducer = (prev: unknown, next: unknown) => unknown;
export type StreamReducerMap = Record<string, StreamReducer>;

const BASE_STREAMING_REDUCERS: StreamReducerMap = {
  agentIntents: replace,
  creditStatus: replace,
  status: replace,
  phases: replace,
  // Transient "compaction is running" flag — emitted by the server as a
  // `__state_patch__` writer event while compaction runs, cleared when done.
  // Read via `useIsCompacting()`.
  isCompacting: replace,
  tasks: mergeByKey("name"),
  // The artifacts channel is base-level (every graph carries it); mirror
  // the server's artifactsMerge so a new block streamed mid-run doesn't
  // wipe earlier ones off screen until the parent merge settles.
  artifacts: (prev, next) =>
    artifactsMerge(next as Artifact[], prev as Artifact[] | undefined),
};

/** Fold a flat {key: value} patch into prev via the reducer map. */
function foldPatch(
  prev: any,
  patch: Record<string, unknown>,
  reducers: StreamReducerMap
) {
  const merged = { ...prev };
  for (const [key, value] of Object.entries(patch)) {
    merged[key] = (reducers[key] ?? replace)(prev?.[key], value);
  }
  return merged;
}

/** onUpdateEvent handler: fold known keys from each streamed node output. */
function makeOnUpdateEvent(reducers: StreamReducerMap) {
  return (
    data: Record<string, Record<string, unknown>>,
    { mutate }: { mutate: (fn: (prev: any) => any) => void }
  ) => {
    for (const nodeOutput of Object.values(data)) {
      if (!nodeOutput || typeof nodeOutput !== "object") continue;
      const output = nodeOutput as Record<string, unknown>;
      const patch: Record<string, unknown> = {};
      for (const key of Object.keys(reducers)) {
        if (output[key] !== undefined) patch[key] = output[key];
      }
      if (Object.keys(patch).length > 0) {
        mutate((prev: any) => foldPatch(prev, patch, reducers));
      }
    }
  };
}

/**
 * onCustomEvent handler for deepagents `__state_patch__` events — writer()-
 * emitted flat state patches that bypass the Promise.all batching of normal
 * updates during parallel subagent execution (e.g. each coder flipping its
 * todo as it finishes). Fold them through the same reducer map so the merge
 * semantics are identical regardless of transport.
 */
function makeOnStatePatchHandler(reducers: StreamReducerMap) {
  return (
    event: unknown,
    { mutate }: { mutate: (fn: (prev: any) => any) => void }
  ) => {
    const payload = event as Record<string, unknown> | null;
    if (!payload || payload.event !== "__state_patch__") return;
    const { id: _id, event: _event, ...patch } = payload;
    if (Object.keys(patch).length > 0) {
      mutate((prev: any) => foldPatch(prev, patch, reducers));
    }
  };
}

interface ProjectStreamConfig<TState extends Record<string, unknown>> {
  /** Graph name — must match registerGraph() on langgraph-server.ts */
  assistantId: string;
  /** Callback when the server assigns a threadId (for URL updates) */
  onThreadId?: (threadId: string) => void;
  /** Override threadId resolution. Default: reads from page props */
  getInitialThreadId?: () => string | undefined;
  /** Whether to fetch state history on mount. Default: true */
  fetchStateHistory?: boolean;
  /** Whether to reconnect on mount. Default: true */
  reconnectOnMount?: boolean;
  /** Custom onUpdateEvent handler. Default folds keys from the (base +
   *  `streamingReducers`) map out of subgraph node outputs. Pass `false` to
   *  disable. Prefer `streamingReducers` over a bespoke handler. */
  onUpdateEvent?: false | useLanggraphStreamOptions<TState>["onUpdateEvent"];
  /** Custom onCustomEvent handler. When omitted and `streamingReducers` is set,
   *  a default handler folds deepagents `__state_patch__` events via the map. */
  onCustomEvent?: useLanggraphStreamOptions<TState>["onCustomEvent"];
  /** Per-chat streaming reducers, merged over the base map. Register a key
   *  (e.g. `todos: merge-by-id`) instead of writing a bespoke onUpdateEvent. */
  streamingReducers?: StreamReducerMap;
}

/** Common page props shape for chat pages */
interface ChatPageProps {
  thread_id?: string;
  /** True when Rails has minted thread_id but Langgraph has no checkpoint
   *  yet (e.g. picker pre-creates Chat before user sends a message). The
   *  SDK runs in deferred mode: skips /state hydration; forwards the id on
   *  first submit so LG creates the thread WITH our id. */
  thread_reserved?: boolean;
  jwt?: string;
  langgraph_path?: string;
  [key: string]: unknown;
}

export function useProjectStreamOptions<
  TState extends Record<string, unknown> = Record<string, unknown>
>(config: ProjectStreamConfig<TState>): useLanggraphStreamOptions<TState> {
  const {
    assistantId,
    onThreadId,
    getInitialThreadId,
    fetchStateHistory = false,
    reconnectOnMount = true,
    onUpdateEvent,
    onCustomEvent,
    streamingReducers,
  } = config;

  const page = usePage<ChatPageProps>();
  const storeJwt = useJwt();
  const storeLanggraphPath = useLanggraphPath();
  const jwt = storeJwt ?? page.props.jwt;
  const langgraphPath = storeLanggraphPath ?? page.props.langgraph_path;

  // Resolve threadId on mount only — avoids controlled/uncontrolled footgun.
  // See context.md lesson #1: if you pass threadId: undefined, the SDK enters
  // controlled mode and ignores its own internal state. We omit the key
  // entirely for new threads.
  const [mountThreadId] = useState<string | undefined>(() => {
    if (getInitialThreadId) return getInitialThreadId();
    return page.props.thread_id || undefined;
  });
  // Resolve "is this thread reserved-but-not-materialized?" at mount too.
  // Locking it on mount means a stream-end refresh that flips
  // thread_reserved to false (because LG now has checkpoints) doesn't yank
  // the SDK between deferred and live modes mid-session.
  const [mountThreadReserved] = useState<boolean>(
    () => Boolean(page.props.thread_reserved)
  );

  return useMemo(() => {
    const opts: useLanggraphStreamOptions<TState> = {
      apiUrl: langgraphPath ?? "",
      assistantId,
      defaultHeaders: { Authorization: `Bearer ${jwt}` },
      name: assistantId,
      reconnectOnMount,
      fetchStateHistory,
    };

    if (onThreadId) opts.onThreadId = onThreadId;

    opts.onError = (error: unknown) => {
      // Credit errors are handled by CreditWarningModal, not Sentry
      const msg = error instanceof Error ? error.message : String(error);
      if (msg.includes("CREDITS_EXHAUSTED") || msg.includes("Insufficient credits")) return;

      Sentry.captureException(error, {
        tags: { assistantId, source: "useStream" },
      });
    };

    // onUpdateEvent: default merges agentIntents/creditStatus
    // from subgraph node outputs so ChatProvider side-effect hooks react mid-run.
    const reducers = streamingReducers
      ? { ...BASE_STREAMING_REDUCERS, ...streamingReducers }
      : BASE_STREAMING_REDUCERS;

    if (onUpdateEvent !== false) {
      (opts as any).onUpdateEvent = onUpdateEvent ?? makeOnUpdateEvent(reducers);
    }

    // Only attach a custom-event handler when there's something to fold —
    // keeps the SDK from subscribing to the custom stream mode for chats that
    // don't emit __state_patch__.
    if (onCustomEvent) {
      (opts as any).onCustomEvent = onCustomEvent;
    } else if (streamingReducers) {
      (opts as any).onCustomEvent = makeOnStatePatchHandler(reducers);
    }

    // Pass threadId + threadReserved straight through — the library handles
    // mode selection (live vs reserved-deferred vs uncontrolled).
    if (mountThreadId) opts.threadId = mountThreadId;
    if (mountThreadReserved) opts.threadReserved = true;

    return opts;
  }, [mountThreadId, mountThreadReserved, jwt, langgraphPath, assistantId, onThreadId, onUpdateEvent, onCustomEvent, streamingReducers, reconnectOnMount, fetchStateHistory]);
}
